import { Prop } from '@Ravnur-Inc/decorators';
import { Vue, Options } from 'vue-class-component';

import { $Types } from '../../typings/tsx';

import './toggler.scss';

type Mode = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'grey';

interface Toggler$Option<V> {
  id: V;
  label: string;
  mode?: Mode;
}

export type Toggler$Options<V> = [Toggler$Option<V>, Toggler$Option<V>];

type Props<V> = {
  value?: V;
  options: Toggler$Options<V>;
  label?: string;
  disabled?: boolean;
};

type Emits<V> = {
  onInput?: (val: V) => void;
};

const CN = 'toggler';

@Options({
  emits: ['input', 'update:value'],
  inheritAttrs: false,
})
export default class Toggler<V> extends Vue {
  declare $props: $Types<Props<V>, Emits<V>, 'value'>;

  @Prop({ default: undefined }) value: V;
  @Prop({}) options!: Toggler$Options<V>;
  @Prop({ default: '' }) label: string;
  @Prop({ default: false }) disabled: string;

  private get isRight() {
    const { options, value } = this;
    const right = options[1];
    return right.id === value;
  }

  private get current() {
    const [left, right] = this.options;
    return this.isRight ? right : left;
  }

  private get defaultMode(): Mode {
    return this.isRight ? 'primary' : 'danger';
  }

  render() {
    const mode = this.current.mode || this.defaultMode;

    const cn = {
      [CN]: true,
      [`${CN}--${mode}`]: mode,
      [`${CN}--right`]: this.isRight,
      [`${CN}--left`]: !this.isRight,
      [`${CN}--disabled`]: this.disabled,
    };

    return (
      <div class={cn} {...this.$attrs}>
        <label class={[`${CN}__title`]}>{this.label}</label>
        <div class={`${CN}__badge`} onClick={this.toggle}>
          <span class={`${CN}__pointer`} />
          {this.current.label}
        </div>
      </div>
    );
  }

  private toggle() {
    if (this.disabled) {
      return;
    }

    const { isRight, options } = this;
    const [left, right] = options;
    const val = isRight ? left.id : right.id;
    this.$emit('input', val);
    this.$emit('update:value', val);
  }
}
